import { Entity } from '@common/types/Entity';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { ChevronLeftDoubleIcon, ChevronRightDoubleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { useOnMount } from '@prophecy/utils/react/hooks';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { DatasetEntityType, FileFormats } from '../../config';
import { MockPipelineComponent } from '../../MockWorkflow/MockWorkflow';
import { MockWorkflowContextType, MockWorkflowProvider } from '../../MockWorkflow/mockWorkFlowContext';
import { useImportState } from '../useImportState';
import { AddAllPipelines } from './components/AddAllPipelines';
import { EmptyForm } from './components/EmptyForm';
import { EntityForm } from './components/EntityForm';
import { ProjectBrowser } from './components/ProjectBrowser';
import { UploadFile } from './components/UploadFile';
import { ValidateDialog, ValidateSkipDialog } from './components/ValidateDialog';
import { SearchInput } from './utils';

const StyledStack = styled(Stack)`
  border-right: 1px solid ${theme.colors.gray200};
  padding-top: ${theme.spaces.x12} 0;
`;

const StyledStackItem = styled(StackItem)`
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${theme.spaces.x12};
`;

const AddAllPipelinesContainer = styled(Stack)`
  padding: ${theme.spaces.x16};
  border-top: 1px solid ${theme.colors.gray200};
`;

export const ChooseEntities = () => {
  const {
    browserDataLoading,
    projectBrowserState: { _setProjectBrowserWidth, projectBrowserWidth },
    transpilerState: {
      transpilerEntityTypes: { pipelines, jobs, datasets = [] }
    },
    transpileGraphState: {
      clientState: {
        savedEntities: { associatedEntitiesLoaded }
      },
      actions: { initializeConnection: initializeTranspilerWS }
    },
    formDataWithActions: {
      formContext: { getValues }
    }
  } = useImportState();

  const isSql = getValues('language') === BackEndCodeLanguage.sql;
  const isAlteryx = getValues('source') === FileFormats.alteryx;

  const globalConf = useGlobalVariables();
  const hasDatasets = globalConf.isAlteryxDatasetEnabled;

  const [searchName, setSearchStr] = useState('');
  const [mountMockWorkflow, setMountMockWorkflow] = useState(false);
  const [disableConfigButton, setDisableConfigButton] = useState(false);

  const hasFileEntites = jobs?.length > 0 || pipelines?.length > 0 || datasets.length > 0;

  const hasProjectBrowser = projectBrowserWidth > 0;

  const currentPipelines = pipelines.filter(({ name }) => name?.toLowerCase().indexOf(searchName?.toLowerCase()) > -1);
  const currentJobs = jobs.filter(({ name }) => name?.toLowerCase().indexOf(searchName?.toLowerCase()) > -1);
  const currentDatasets = hasDatasets
    ? datasets.filter(({ name }) => name?.toLowerCase().indexOf(searchName?.toLowerCase()) > -1)
    : [];

  const currentDatasetRef = useRef<DatasetEntityType | null>(null);
  const handleDatasetConfig = async function (dataset: DatasetEntityType) {
    currentDatasetRef.current = dataset;
    if (!mountMockWorkflow) {
      setMountMockWorkflow(true);
      return;
    }
    const process = await mockWorkflowContextValues.callbackRef.current!.createGem(dataset);
    if (process) {
      const gemId = process.id!;
      mockWorkflowContextValues.callbackRef.current!.openDialog(gemId);
    }
  };

  const mockWorkflowCallbackRef = useRef<MockWorkflowContextType['callbackRef']['current']>({
    getWorkflowStore: () => ({}),
    createGem: (_dataset: DatasetEntityType) => Promise.resolve({}),
    openDialog: (_processId: string) => {},
    updateCurrentDataset: () => {},
    handleDatasetConfig: async (_dataset: DatasetEntityType) => {},
    disableConfigButton: (disable) => {
      setDisableConfigButton(disable);
    }
  });

  // updating function everytime due to closure issue
  mockWorkflowCallbackRef.current!.handleDatasetConfig = handleDatasetConfig;
  const handledatasetConfigOnMountRef = useRef(true);
  const wipStateRef = useRef(null);

  const mockWorkflowContextValues: MockWorkflowContextType = {
    callbackRef: mockWorkflowCallbackRef,
    currentDatasetRef,
    handledatasetConfigOnMountRef,
    wipStateRef
  };

  useOnMount(() => {
    if (!associatedEntitiesLoaded) {
      initializeTranspilerWS();
    }
  });

  return (
    <Stack width='100%' height='100%' align='space-between' direction='horizontal'>
      <ValidateDialog />
      <ValidateSkipDialog />
      {isAlteryx && mountMockWorkflow && !isSql ? (
        <MockWorkflowProvider value={mockWorkflowContextValues}>
          <MockPipelineComponent hasCodeGeneration={false} currentPipelines={currentPipelines} />
        </MockWorkflowProvider>
      ) : null}
      {hasProjectBrowser && (
        <StyledStack height='100%' gap={theme.spaces.x16} align='space-between'>
          <StyledStackItem grow='1' shrink='1'>
            <ProjectBrowser />
          </StyledStackItem>
          <Stack>
            {!browserDataLoading && (
              <AddAllPipelinesContainer>
                <AddAllPipelines />
              </AddAllPipelinesContainer>
            )}
            <UploadFile isBrowser={true} />
          </Stack>
        </StyledStack>
      )}
      <StackItem grow='1'>
        <Stack gap={theme.spaces.x24} padding={theme.spaces.x32} width='100%' height='100%'>
          <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
            <Button
              variant='plain'
              onClick={() => _setProjectBrowserWidth(hasProjectBrowser ? 0 : 310)}
              icon={
                hasProjectBrowser ? <ChevronRightDoubleIcon type='default' /> : <ChevronLeftDoubleIcon type='default' />
              }
              aria-label={hasProjectBrowser ? 'Hide project browser' : 'Show project browser'}
              data-test-id='choose-entities-toggle-project-browser-button'
            />
            <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
              <Text level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                Entities for Transpilation
              </Text>
              <SearchInput
                placeholder={
                  isAlteryx && hasDatasets
                    ? `Search ${isSql ? Entity.Table : Entity.Dataset}, ${isSql ? Entity.Model : Entity.Pipeline}  or Job`
                    : `Search ${isSql ? Entity.Model : Entity.Pipeline}  or Job`
                }
                value={searchName}
                onChange={setSearchStr}
              />
            </Stack>
          </Stack>
          <StackItem grow='1'>
            {hasFileEntites ? (
              <EntityForm
                data={{
                  jobs: currentJobs,
                  pipelines: currentPipelines,
                  datasets: currentDatasets
                }}
                handleDatasetConfig={handleDatasetConfig}
                disableConfigButton={disableConfigButton}
              />
            ) : (
              <EmptyForm />
            )}
          </StackItem>
        </Stack>
      </StackItem>
    </Stack>
  );
};
